/**
 * Configuration service for environment-specific settings
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  loginUrl: string;
  returnUrl: string;
  environment: 'development' | 'preprod' | 'production';
}

/**
 * Get configuration based on current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const environment = (process.env.REACT_APP_ENVIRONMENT || 'development') as EnvironmentConfig['environment'];
  
  // Get base URLs from environment variables or use defaults
  const baseConfig = {
    environment,
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || getDefaultApiUrl(),
    returnUrl: process.env.REACT_APP_RETURN_URL || window.location.origin,
  };

  // Configure login URL based on environment
  const loginUrls = {
    development: 'https://preprodgo.accessacloud.com/login', // Use preprod for development
    preprod: 'https://preprodgo.accessacloud.com/login',
    production: 'https://go.accessacloud.com/login', // Production Access Group URL
  };

  const config = {
    ...baseConfig,
    loginUrl: process.env.REACT_APP_LOGIN_URL || loginUrls[environment],
  };
  
  return config;
}

/**
 * Get default API URL based on environment
 */
function getDefaultApiUrl(): string {
  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  
  // Use localhost for local development (NODE_ENV=development)
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:7071/api';
  }
  
  // For deployed environments, use environment-specific URLs or relative path
  // This handles both preprod and production deployments which both use NODE_ENV=production
  switch (environment) {
    case 'preprod':
      // Use relative path for preprod deployment
      return '/api';
    case 'production':
      // Use relative path for production deployment  
      return '/api';
    default:
      // Fallback to relative path
      return '/api';
  }
}

/**
 * Get the full Access Group login URL with return URL
 */
export function getLoginRedirectUrl(): string {
  const config = getEnvironmentConfig();
  return `${config.loginUrl}?ReturnUrl=${encodeURIComponent(config.returnUrl)}`;
}

/**
 * Check if we're running in a development environment
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development';
}

/**
 * Check if we're running in preprod environment
 */
export function isPreprod(): boolean {
  return process.env.REACT_APP_ENVIRONMENT === 'preprod';
}

/**
 * Check if we're running in production environment
 */
export function isProduction(): boolean {
  return process.env.REACT_APP_ENVIRONMENT === 'production';
}

// Export singleton config
export const config = getEnvironmentConfig();
